<i18n locale="en">
{
  "gated": {
    "body": "Upgrade to embed OpenTable or Resy, or take reservations with an Owner-native form.",
    "cta": "See what's included",
    "title": "Reservations are included with Max",
    "unavailable": "Reservations aren't available on your plan."
  },
  "provider-type": {
    "label": "Provider Type"
  },
  "provider-id": {
    "label": "Provider ID",
    "placeholder": "This is automatically detected"
  },
  "provider-api-key": {
    "label": "API Key",
    "placeholder": "This is automatically detected"
  },
  "embed-code": {
    "label": "Widget Code",
    "helper-opentable": "Copy and paste the embed code from OpenTable and we'll detect the provider ID.",
    "helper-resy": "Copy and paste the embed code from Resy and we'll detect the venue ID and API key.",
    "placeholder": "Paste code here"
  },
  "validation": {
    "required": "This field is required",
    "invalid-code": "Invalid code snippet",
    "invalid-url-opentable": "Code snippet must be a valid OpenTable code snippet",
    "invalid-url-resy": "Code snippet must be a valid Resy code snippet",
    "invalid-provider-id": "No provider ID detected",
    "invalid-api-key": "No API key detected"
  }
}
</i18n>

<template>
  <div
    v-if="isGated"
    class="flex-col items-start gap-4"
    data-testid="reservation-embed-gate"
  >
    <template v-if="showBoostUpsell">
      <UpsellGateBadge />
      <OwnType :text="t('gated.title')" variant="title-regular" />
      <OwnType
        :text="t('gated.body')"
        variant="paragraph-small"
        color="secondary"
      />
      <OwnButton
        :text="t('gated.cta')"
        variant="primary"
        data-testid="reservation-embed-upsell-button"
        @click="openUpsellSheet"
      />
    </template>
    <OwnType
      v-else
      :text="t('gated.unavailable')"
      variant="paragraph-small"
      color="secondary"
    />
  </div>
  <div v-else class="flex-col gap-8">
    <OwnInputContainer :label="t('provider-type.label')">
      <OwnSelect
        v-model="selectedProviderType"
        :options="providerTypeOptions"
        @update:model-value="onProviderTypeChange"
      />
    </OwnInputContainer>

    <OwnInputContainer
      :label="t('embed-code.label')"
      :helper-text="
        selectedProviderType === 'resy'
          ? t('embed-code.helper-resy')
          : t('embed-code.helper-opentable')
      "
      :errors="mapErrorMessage(formState.embedCode.$errors)"
      :value="embedCode"
    >
      <OwnTextarea
        v-model="embedCode"
        rows="4"
        :placeholder="t('embed-code.placeholder')"
        @update:model-value="onEmbedCodeUpdate"
      />
    </OwnInputContainer>

    <OwnInputContainer :label="t('provider-id.label')" :value="providerId">
      <OwnInput
        v-model="providerId"
        disabled
        :placeholder="t('provider-id.placeholder')"
      />
    </OwnInputContainer>

    <OwnInputContainer
      v-if="selectedProviderType === 'resy'"
      :label="t('provider-api-key.label')"
      :value="providerApiKey"
    >
      <OwnInput
        v-model="providerApiKey"
        disabled
        :placeholder="t('provider-api-key.placeholder')"
      />
    </OwnInputContainer>
  </div>
</template>

<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { isBrandBoostOnly } from '@/features/billing/queries/useActiveBrandPlans'
import { MIB_GATES_FLAG } from '@/features/upsell/featureFlags'
import UpsellGateBadge from '@/features/upsell/public/UpsellGateBadge.vue'
import { openUpsellSheet } from '@/features/upsell/sheetState'
import { isReservationsGated } from '@/features/website-builder/gating'
import { OwnButton } from '@/ui/OwnButton'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnSelect } from '@/ui/OwnSelect'
import { OwnTextarea } from '@/ui/OwnTextarea'
import { OwnType } from '@/ui/OwnType'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'
import { mapErrorMessage } from '@/utils/vuelidate'

import type { ReservationEmbedSection } from '../../../WebsiteBuilder.types'
import { ReservationEmbedCommands } from '../commands/ReservationEmbedCommands'

const props = defineProps<{
  id: string
}>()

const { t } = useI18n()
const store = useStore()

const { enabled: mibGatesEnabled } = useReactiveFeatureFlag(MIB_GATES_FLAG)

const isGated = computed(() => isReservationsGated(mibGatesEnabled.value))

// Boost-only brands get the upsell pitch; any other gated brand just sees a
// quiet unavailable note.
const showBoostUpsell = computed(() => isGated.value && isBrandBoostOnly())

const providerId = ref('')
const providerApiKey = ref('')
const embedCode = ref('')
const selectedProviderType = ref<'opentable' | 'resy'>('opentable')

const providerTypeOptions = [
  { label: 'OpenTable', value: 'opentable' },
  { label: 'Resy', value: 'resy' },
]

const isValidScriptTag = (value: string) => {
  const hasOpeningTag = value.includes('<script')
  const hasClosingTag = value.includes('/script>')
  return hasOpeningTag && hasClosingTag
}

const isValidOpenTableURL = (value: string) => {
  return value.includes('opentable.com')
}

const isValidResyURL = (value: string) => {
  return value.includes('resy.com') || value.includes('resy-widget')
}

const extractOpenTableProviderId = (embedCodeValue: string): string | null => {
  const ridMatch = embedCodeValue.match(/rid=([^&]+)/)
  return ridMatch && ridMatch[1] ? ridMatch[1] : null
}

const extractResyIds = (
  embedCodeValue: string
): { apiKey: string | null; venueId: string | null } => {
  const venueIdMatch =
    embedCodeValue.match(/"venueId"\s*:\s*(\d+)/) ||
    embedCodeValue.match(/venueId:\s*['"]([^'"]+)['"]/)
  const venueId = venueIdMatch && venueIdMatch[1] ? venueIdMatch[1] : null

  const apiKeyMatch =
    embedCodeValue.match(/"apiKey"\s*:\s*"([^"]+)"/) ||
    embedCodeValue.match(/apiKey:\s*['"]([^'"]+)['"]/)

  const apiKey = apiKeyMatch && apiKeyMatch[1] ? apiKeyMatch[1] : null

  return { apiKey, venueId }
}

const hasValidProviderId = (embedCodeValue: string): boolean => {
  if (selectedProviderType.value === 'opentable') {
    const providerId = extractOpenTableProviderId(embedCodeValue)
    return providerId !== null
  }

  if (selectedProviderType.value === 'resy') {
    const { venueId } = extractResyIds(embedCodeValue)
    return venueId !== null
  }

  return false
}

const hasValidApiKey = (embedCodeValue: string) => {
  if (selectedProviderType.value === 'resy') {
    const { apiKey } = extractResyIds(embedCodeValue)
    return apiKey !== null
  }
  return true
}

const validateURL = (value: string) => {
  return selectedProviderType.value === 'resy'
    ? isValidResyURL(value)
    : isValidOpenTableURL(value)
}

const rules = computed(() => {
  // Get the current message based on provider type
  const urlMessage =
    selectedProviderType.value === 'resy'
      ? t('validation.invalid-url-resy')
      : t('validation.invalid-url-opentable')

  return {
    embedCode: {
      required: helpers.withMessage(t('validation.required'), required),
      validApiKey: helpers.withMessage(
        t('validation.invalid-api-key'),
        hasValidApiKey
      ),
      validProviderId: helpers.withMessage(
        t('validation.invalid-provider-id'),
        hasValidProviderId
      ),
      validScript: helpers.withMessage(
        t('validation.invalid-code'),
        isValidScriptTag
      ),
      validURL: helpers.withMessage(urlMessage, validateURL),
    },
  }
})

const formState = useVuelidate(rules, { embedCode })

const currentBlockParams = computed(() => {
  return store.getters['websiteBuilder/blockById'](props.id)
    ?.params as ReservationEmbedSection['params']
})

const onProviderTypeChange = async (value: string | number) => {
  const newProviderType = value as 'opentable' | 'resy'
  formState.value.$reset()
  selectedProviderType.value = newProviderType

  providerId.value = ''
  providerApiKey.value = ''
  embedCode.value = ''

  await nextTick()
}

const onEmbedCodeUpdate = async (value: string) => {
  embedCode.value = value

  // Re-validate to ensure we're using the current provider type rules
  const isValid = await formState.value.$validate()

  if (!isValid) return

  if (selectedProviderType.value === 'opentable') {
    const extractedProviderId = extractOpenTableProviderId(value)
    if (!extractedProviderId) return
    await updateBlockParams({
      providerId: extractedProviderId,
      providerType: 'opentable',
    })
  }

  if (selectedProviderType.value === 'resy') {
    const { apiKey, venueId } = extractResyIds(value)
    if (!venueId || !apiKey) return
    await updateBlockParams({
      providerApiKey: apiKey,
      providerId: venueId,
      providerType: 'resy',
    })
  }
}

const updateBlockParams = async (params: {
  providerApiKey?: string
  providerId: string
  providerType: 'opentable' | 'resy'
}) => {
  providerId.value = params.providerId
  providerApiKey.value = params.providerApiKey ?? ''
  await ReservationEmbedCommands.updateBlock({
    targetId: props.id,
    update: {
      params: {
        providerId: params.providerId,
        providerType: params.providerType,
        ...(params.providerType === 'resy' && {
          providerApiKey: params.providerApiKey ?? null,
        }),
      },
    },
  })
}

onMounted(() => {
  const params = currentBlockParams.value
  if (params) {
    selectedProviderType.value = params.providerType ?? 'opentable'
    providerId.value = params.providerId ?? ''

    if (selectedProviderType.value === 'resy' && 'providerApiKey' in params) {
      providerApiKey.value = params.providerApiKey ?? ''
    }
  }
})
</script>
